import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import { flushSync } from 'react-dom';
import api, { axiosInstance } from '../api/apiClient';
import { toast } from 'react-toastify';
import { getDefaultRouteForRole, getRoleName } from '../utils/roleRedirect';
import { sanitizeAuthUser, hasStoredAuthSession, getStoredAuthUser } from '../utils/authStorage';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [refreshToken, setRefreshToken] = useState(null);
  const [loading, setLoading] = useState(true);

  // Initialize auth state from localStorage on component mount
  useEffect(() => {
    const initializeAuth = async () => {
      const storedToken = localStorage.getItem('accessToken');
      const storedRefreshToken = localStorage.getItem('refreshToken');
      const storedUser = localStorage.getItem('user');

      if (storedToken && storedUser) {
        try {
          const parsedUser = JSON.parse(storedUser);

          // Validate token with backend
          const response = await api.auth.validateToken();
          if (response.data.success) {
            // Token is valid, use fresh user data from backend
            const validatedUser = response.data.data.user;

            setToken(storedToken);
            setRefreshToken(storedRefreshToken);
            setUser(validatedUser);

            // Update localStorage with fresh user data
            localStorage.setItem('user', JSON.stringify(validatedUser));
          } else {
            // Token invalid, clear auth data
            clearAllAuthData();
          }
        } catch (error) {
          // Token validation failed, clear auth data
          clearAllAuthData();
        }
      }
      setLoading(false);
    };

    initializeAuth();
  }, []);

  // Login function
  const login = useCallback(async (email, password) => {
    try {
      setLoading(true);

      // Ensure email and password are properly formatted
      const loginData = {
        email: email.toString().trim().toLowerCase(),
        password: password.toString().trim()
      };

      const response = await api.auth.login(loginData.email, loginData.password);

      if (response.data.success && response.data.data) {
        const nextStep = response.data.data.next_step;

        if (nextStep === 'SETUP_2FA' || nextStep === 'VERIFY_OTP') {
          const tempToken = response.data.data.temp_token;
          const userPreview = response.data.data.user;

          if (!tempToken || !userPreview) {
            throw new Error('Invalid 2FA response from server');
          }

          sessionStorage.setItem('twofa_temp_token', tempToken);
          sessionStorage.setItem('twofa_user_preview', JSON.stringify(userPreview));
          toast.info(response.data.message || 'Two-factor authentication required');

          return {
            success: true,
            data: {
              next_step: nextStep,
              temp_token: tempToken,
              user: userPreview
            }
          };
        }

        const { user: userData, tokens } = response.data.data;

        // Validate that we received the expected data structure
        if (!userData || !tokens || !tokens.access_token) {
          throw new Error('Invalid response structure from server');
        }

        const safeUser = sanitizeAuthUser(userData);

        // Set auth state (synchronous commit before any navigation)
        flushSync(() => {
          setUser(safeUser);
          setToken(tokens.access_token);
          setRefreshToken(tokens.refresh_token || null);
        });

        // Store in localStorage
        localStorage.setItem('accessToken', tokens.access_token);
        if (tokens.refresh_token) {
          localStorage.setItem('refreshToken', tokens.refresh_token);
        }
        localStorage.setItem('user', JSON.stringify(safeUser));

        sessionStorage.removeItem('twofa_temp_token');
        sessionStorage.removeItem('twofa_user_preview');

        toast.success(response.data.message || 'Login successful!');
        return {
          success: true,
          data: {
            user: safeUser,
            tokens
          }
        };
      } else {
        const errorMessage = response.data.message || 'Login failed. Please try again.';
        toast.error(errorMessage);
        return { success: false, error: errorMessage };
      }
    } catch (error) {
      console.error('Login error:', error.response?.data?.message || error.message);

      let errorMessage = 'Login failed. Please try again.';

      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.status === 401) {
        errorMessage = 'Invalid email or password';
      } else if (error.response?.status === 500) {
        errorMessage = 'Server error. Please try again later.';
      } else if (error.code === 'ECONNREFUSED' || error.code === 'ERR_NETWORK') {
        errorMessage = 'Cannot connect to server. Please check your connection.';
      } else if (error.message) {
        errorMessage = error.message;
      }

      toast.error(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  }, []);

  // Login with Google function
const loginWithGoogle = useCallback(async (googleCredential) => {
  try {
    setLoading(true);

    const response = await api.auth.googleLogin(googleCredential);

    if (response.data.success && response.data.data) {
      const nextStep = response.data.data.next_step;

      if (nextStep === 'SETUP_2FA' || nextStep === 'VERIFY_OTP') {
        const tempToken = response.data.data.temp_token;
        const userPreview = response.data.data.user;

        if (!tempToken || !userPreview) {
          throw new Error('Invalid 2FA response from server');
        }

        sessionStorage.setItem('twofa_temp_token', tempToken);
        sessionStorage.setItem('twofa_user_preview', JSON.stringify(userPreview));
        toast.info(response.data.message || 'Two-factor authentication required');

        return {
          success: true,
          data: {
            next_step: nextStep,
            temp_token: tempToken,
            user: userPreview
          }
        };
      }

      const { user: userData, tokens } = response.data.data;

      if (!userData || !tokens || !tokens.access_token) {
        throw new Error('Invalid response structure from server');
      }

      const safeUser = sanitizeAuthUser(userData);

      flushSync(() => {
        setUser(safeUser);
        setToken(tokens.access_token);
        setRefreshToken(tokens.refresh_token || null);
      });

      localStorage.setItem('accessToken', tokens.access_token);
      if (tokens.refresh_token) {
        localStorage.setItem('refreshToken', tokens.refresh_token);
      }
      localStorage.setItem('user', JSON.stringify(safeUser));

      sessionStorage.removeItem('twofa_temp_token');
      sessionStorage.removeItem('twofa_user_preview');

      toast.success(response.data.message || 'Signed in with Google!');
      return {
        success: true,
        data: {
          user: safeUser,
          tokens
        }
      };
    } else {
      const errorMessage = response.data.message || 'Google sign-in failed. Please try again.';
      toast.error(errorMessage);
      return { success: false, error: errorMessage };
    }
  } catch (error) {
    console.error('Google login error:', error.response?.data?.message || error.message);

    let errorMessage = 'Google sign-in failed. Please try again.';
    if (error.response?.data?.message) {
      errorMessage = error.response.data.message;
    } else if (error.response?.status === 401) {
      errorMessage = 'Google authentication was rejected';
    } else if (error.response?.status === 500) {
      errorMessage = 'Server error. Please try again later.';
    } else if (error.code === 'ECONNREFUSED' || error.code === 'ERR_NETWORK') {
      errorMessage = 'Cannot connect to server. Please check your connection.';
    } else if (error.message) {
      errorMessage = error.message;
    }

    toast.error(errorMessage);
    return { success: false, error: errorMessage };
  } finally {
    setLoading(false);
  }
}, []);

// Login with Microsoft function
const loginWithMicrosoft = useCallback(async (microsoftIdToken) => {
  try {
    setLoading(true);

    const response = await api.auth.microsoftLogin(microsoftIdToken);

    if (response.data.success && response.data.data) {
      const nextStep = response.data.data.next_step;

      if (nextStep === 'SETUP_2FA' || nextStep === 'VERIFY_OTP') {
        const tempToken = response.data.data.temp_token;
        const userPreview = response.data.data.user;

        if (!tempToken || !userPreview) {
          throw new Error('Invalid 2FA response from server');
        }

        sessionStorage.setItem('twofa_temp_token', tempToken);
        sessionStorage.setItem('twofa_user_preview', JSON.stringify(userPreview));
        toast.info(response.data.message || 'Two-factor authentication required');

        return {
          success: true,
          data: {
            next_step: nextStep,
            temp_token: tempToken,
            user: userPreview
          }
        };
      }

      const { user: userData, tokens } = response.data.data;

      if (!userData || !tokens || !tokens.access_token) {
        throw new Error('Invalid response structure from server');
      }

      const safeUser = sanitizeAuthUser(userData);

      flushSync(() => {
        setUser(safeUser);
        setToken(tokens.access_token);
        setRefreshToken(tokens.refresh_token || null);
      });

      localStorage.setItem('accessToken', tokens.access_token);
      if (tokens.refresh_token) {
        localStorage.setItem('refreshToken', tokens.refresh_token);
      }
      localStorage.setItem('user', JSON.stringify(safeUser));

      sessionStorage.removeItem('twofa_temp_token');
      sessionStorage.removeItem('twofa_user_preview');

      toast.success(response.data.message || 'Signed in with Microsoft!');
      return {
        success: true,
        data: {
          user: safeUser,
          tokens
        }
      };
    } else {
      const errorMessage = response.data.message || 'Microsoft sign-in failed. Please try again.';
      toast.error(errorMessage);
      return { success: false, error: errorMessage };
    }
  } catch (error) {
    console.error('Microsoft login error:', error.response?.data?.message || error.message);

    let errorMessage = 'Microsoft sign-in failed. Please try again.';
    if (error.response?.data?.message) {
      errorMessage = error.response.data.message;
    } else if (error.response?.status === 401) {
      errorMessage = 'Microsoft authentication was rejected';
    } else if (error.response?.status === 500) {
      errorMessage = 'Server error. Please try again later.';
    } else if (error.code === 'ECONNREFUSED' || error.code === 'ERR_NETWORK') {
      errorMessage = 'Cannot connect to server. Please check your connection.';
    } else if (error.message) {
      errorMessage = error.message;
    }

    toast.error(errorMessage);
    return { success: false, error: errorMessage };
  } finally {
    setLoading(false);
  }
}, []);

  // Utility function to clear all authentication data
  const clearAllAuthData = useCallback(() => {
    // Clear all authentication-related data from localStorage
    const authKeys = ['accessToken', 'refreshToken', 'user', 'token', 'auth_token', 'jwt_token', 'moodleToken', 'moodleUserId'];
    authKeys.forEach(key => {
      localStorage.removeItem(key);
      sessionStorage.removeItem(key);
    });

    // Clear state
    setUser(null);
    setToken(null);
    setRefreshToken(null);
  }, []);

  // Logout function
const logout = useCallback(async () => {
  try {
    if (token) {
      await api.auth.logout();
    }
  } catch (error) {
    console.error('AuthContext: Logout error:', error);
    // Continue with logout even if API call fails
  } finally {
    // Clear all authentication state
    clearAllAuthData();
    toast.success('Logged out successfully');
    window.location.href = '/login';
  }
}, [token, clearAllAuthData]);

  // Refresh token function
  const refreshAccessToken = useCallback(async () => {
    try {
      if (!refreshToken) {
        throw new Error('No refresh token available');
      }

      const response = await axiosInstance.post('/auth/refresh-token', {
        refresh_token: refreshToken
      });

      if (response.data.success && response.data.data) {
        const { access_token, token_type, expires_in } = response.data.data;

        if (!access_token) {
          throw new Error('No access token returned from refresh');
        }

        setToken(access_token);
        // Note: Backend doesn't return new refresh token, so we keep the existing one

        localStorage.setItem('accessToken', access_token);

        return access_token;
      } else {
        throw new Error(response.data.message || 'Token refresh failed');
      }
    } catch (error) {
      console.error('Token refresh failed:', error.response?.data?.message || error.message);
      // If refresh fails, logout user
      clearAllAuthData();
      throw error;
    }
  }, [refreshToken, clearAllAuthData]);

  // Update current user profile (including avatar)
  const updateProfile = useCallback(async (payload) => {
    try {
      const formData = new FormData();

      if (payload.first_name !== undefined) formData.append('first_name', payload.first_name);
      if (payload.last_name !== undefined) formData.append('last_name', payload.last_name);
      if (payload.phone !== undefined) formData.append('phone', payload.phone || '');
      if (payload.bio !== undefined) formData.append('bio', payload.bio || '');
      if (payload.organization_name !== undefined) {
        formData.append('organization_name', payload.organization_name);
      }

      if (payload.avatarRemove) {
        formData.append('avatarRemove', 'true');
      } else if (payload.avatarFile) {
        formData.append('avatar', payload.avatarFile);
      }

      const response = await api.auth.updateProfile(formData);

      if (response.data.success) {
        const updatedUser = response.data.data.user;
        setUser(updatedUser);
        localStorage.setItem('user', JSON.stringify(updatedUser));
        toast.success('Profile updated successfully');
        return { success: true, user: updatedUser };
      } else {
        const msg = response.data.message || 'Failed to update profile';
        toast.error(msg);
        return { success: false, error: msg };
      }
    } catch (error) {
      console.error('Update profile error:', error);
      const msg = error.response?.data?.message || 'Failed to update profile';
      toast.error(msg);
      return { success: false, error: msg };
    }
  }, []);

  // Change current user's password
  const changePassword = useCallback(async (currentPassword, newPassword, confirmPassword) => {
    try {
      const response = await api.auth.changePassword(currentPassword, newPassword, confirmPassword);

      if (response.data.success) {
        toast.success(response.data.message || 'Password updated successfully');
        return { success: true };
      } else {
        const msg = response.data.message || 'Failed to change password';
        toast.error(msg);
        return { success: false, error: msg };
      }
    } catch (error) {
      console.error('Change password error (frontend):', error);
      const msg = error.response?.data?.message || 'Failed to change password';
      toast.error(msg);
      return { success: false, error: msg };
    }
  }, []);

  // Get user profile
  const getUserProfile = useCallback(async () => {
    try {
      const response = await axiosInstance.get('/auth/profile');
      if (response.data.success) {
        const userData = response.data.data.user;
        setUser(userData);
        localStorage.setItem('user', JSON.stringify(userData));
        return userData;
      } else {
        throw new Error(response.data.message || 'Failed to get profile');
      }
    } catch (error) {
      console.error('Get profile error:', error);
      if (error.response?.status === 401) {
        clearAllAuthData();
      }
      throw error;
    }
  }, [clearAllAuthData]);

  // Validate current token
  const validateToken = useCallback(async () => {
    try {
      const response = await axiosInstance.get('/auth/validate-token');
      if (response.data.success) {
        const userData = response.data.data.user;
        setUser(userData);
        localStorage.setItem('user', JSON.stringify(userData));
        return userData;
      } else {
        throw new Error(response.data.message || 'Token validation failed');
      }
    } catch (error) {
      console.error('Token validation error:', error);
      clearAllAuthData();
      throw error;
    }
  }, [clearAllAuthData]);

  // Store authenticated session after 2FA setup/verify (updates context + localStorage)
  const completeAuthSession = useCallback(({ user: userData, tokens, message }) => {
    const safeUser = sanitizeAuthUser(userData);
    if (!safeUser || !tokens?.access_token) {
      return null;
    }

    localStorage.setItem('accessToken', tokens.access_token);
    if (tokens.refresh_token) {
      localStorage.setItem('refreshToken', tokens.refresh_token);
    } else {
      localStorage.removeItem('refreshToken');
    }
    localStorage.setItem('user', JSON.stringify(safeUser));

    sessionStorage.removeItem('twofa_temp_token');
    sessionStorage.removeItem('twofa_user_preview');

    flushSync(() => {
      setUser(safeUser);
      setToken(tokens.access_token);
      setRefreshToken(tokens.refresh_token || null);
    });

    if (message) {
      toast.success(message);
    }

    return safeUser;
  }, []);

  // Check if user is authenticated (context or persisted session after 2FA)
  const isAuthenticated = useMemo(
    () => (!!token && !!user) || hasStoredAuthSession(),
    [token, user]
  );

  // Role utilities
  const getDefaultRoute = useCallback(
    () => (user ? getDefaultRouteForRole(user.role) : '/'),
    [user]
  );
  const getRole = useCallback(
    () => (user ? getRoleName(user.role) : 'Guest'),
    [user]
  );

  const value = useMemo(() => ({
    user,
    token,
    refreshToken,
    loading,
    login,
    loginWithGoogle,
    loginWithMicrosoft,
    logout,
    refreshAccessToken,
    getUserProfile,
    validateToken,
    updateProfile,
    changePassword,
    clearAllAuthData,
    completeAuthSession,
    isAuthenticated,
    // Role utilities
    getDefaultRouteForRole: getDefaultRoute,
    getRoleName: getRole
  }), [
    user,
    token,
    refreshToken,
    loading,
    login,
    loginWithGoogle,
    loginWithMicrosoft,
    logout,
    refreshAccessToken,
    getUserProfile,
    validateToken,
    updateProfile,
    changePassword,
    clearAllAuthData,
    completeAuthSession,
    isAuthenticated,
    getDefaultRoute,
    getRole
  ]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
